import {
  type AvailableAction,
  type InterpolationVariables,
  interpolateValues,
  resourceActions
} from '@takeshape/actions';
import type { AccessPolicyDocument, Statement } from '@takeshape/schema';
import { arrayContainsAll, setIsEqual } from '@takeshape/util';
import difference from 'lodash/difference';
import forEach from 'lodash/forEach';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import intersection from 'lodash/intersection';
import memoize from 'lodash/memoize';
import uniq from 'lodash/uniq';
import { getAccessPolicy, getActionNamespace, getResourceNamespace } from '../permissions/util.js';
import type { Actions, Permissions, ResourceActions } from './user-role-schema.js';

const getResourceKey = (resource: string): string => {
  const nameSpace = getResourceNamespace(resource);
  return get(resourceActions, [nameSpace, 'resource']) || '';
};

const removeNamespace = (resource: string): string => {
  const parts = resource.split(':');
  return parts.slice(2).join(':');
};

const getTemplateValues = (resource: string): Record<string, string> => {
  const resourceKey = getResourceKey(resource);
  const resourceTemplates = removeNamespace(resourceKey);
  const resourceValues = removeNamespace(resource);
  const pattern = /[^{}]+(?=})/g;
  const templates = resourceTemplates.match(pattern);
  const values = resourceValues.split(':');
  let templateValues = {};
  forEach(templates, (template: string, idx: number) => {
    templateValues = {
      ...templateValues,
      [template]: values[idx] || '*'
    };
  });
  return templateValues;
};

export function normalizeWildcards(resource: string): string {
  return resource.replace(/(:\*)+$/, ':*');
}

export function getResourceString(namespace: string, data: InterpolationVariables): string | undefined {
  const resourceDefinition = resourceActions[namespace];
  if (resourceDefinition) {
    return interpolateValues(resourceDefinition.resource, data);
  }
}

const getNamespaceStarResource = (resource: string): string => {
  const [ts, namespace, projectId] = resource.split(':');
  return `${ts}:${namespace}:${projectId}:*`;
};

const getNamespaceActions = memoize((namespace: string): string[] => {
  const actions = get(resourceActions, [namespace, 'actions'], [] as AvailableAction[]);
  return actions.map((a: AvailableAction) => `${namespace}:${typeof a === 'object' ? a.verb : a}`);
});

function actionsToArray(actions: Actions): string[] {
  return Object.keys(actions).filter((action) => actions[action]);
}

function getDisabledActions(namespace: string, enabled: string[]): string[] {
  return difference(getNamespaceActions(namespace), enabled);
}

function collapseActions(actions: string[]): string[] {
  const namespaceActions = groupBy(actions, getActionNamespace);
  let result: string[] = [];

  forEach(namespaceActions, (actions, namespace) => {
    result = result.concat(getDisabledActions(namespace, actions).length === 0 ? [`${namespace}:*`] : actions);
  });
  return result;
}

export const getRoleFormPermissions = (policy: AccessPolicyDocument): Permissions | null => {
  const accessPolicy = getAccessPolicy(policy);

  const actions: ResourceActions = {};
  for (const s of policy.Statement) {
    if (s.Resource?.length) {
      for (const r of s.Resource) {
        const resource = interpolateValues(getResourceKey(r), getTemplateValues(r));
        const namespace = getResourceNamespace(resource);
        const allActions = getNamespaceActions(namespace);
        actions[resource] = allActions.reduce((result, action) => {
          return { ...result, [action]: accessPolicy({ resource, action }) };
        }, {});
      }
    }
  }
  return { actions };
};

export function getStarResourceAction(
  namespace: string,
  permissions: Permissions
): { resource: string; enabled: string[]; disabled: string[] } | null {
  const { actions } = permissions;
  const resources = Object.keys(actions);
  const projectId = resources[0].split(':')[2];

  const selectAll = getResourceString(namespace, { projectId });
  if (!selectAll) {
    return null;
  }

  const selectAllNormalized = normalizeWildcards(selectAll);
  const selectAllResource = resources.find((resource) => selectAllNormalized === normalizeWildcards(resource));

  if (!selectAllResource) {
    return null;
  }

  const enabled = actions[selectAllResource] ? actionsToArray(actions[selectAllResource]) : [];
  return {
    resource: selectAllResource,
    enabled,
    disabled: getDisabledActions(namespace, enabled)
  };
}

type PolicyState = {
  allow: Record<string, string[]>;
  deny: Record<string, string[]>;
};

export function addNamespaceToPolicy(
  result: PolicyState,
  namespace: string,
  resources: string[],
  permissions: Permissions
): void {
  const star = getStarResourceAction(namespace, permissions);
  if (star) {
    if (star.enabled.length) {
      result.allow[star.resource] = star.enabled;
    }
  }

  for (const resource of resources) {
    if (!star || resource !== star.resource) {
      const actions = permissions.actions[resource];
      const enabled = actions ? actionsToArray(actions) : [];
      const disabled = getDisabledActions(namespace, enabled);
      if (enabled.length) {
        result.allow[resource] = enabled;
      }

      if (star ?? !enabled.length) {
        result.deny[resource] = star ? intersection(disabled, star.enabled) : disabled;
      }
    }
  }
}

function getStatementNamespaceActions(statement: Statement, namespace: string): string[] {
  const hasStarAction = statement.Action?.includes(`${namespace}:*`);
  const allActions = getNamespaceActions(namespace);
  return hasStarAction ? allActions : (statement.Action ?? []).filter((a) => a.split(':')[0] === namespace);
}

/**
 * Conditions:
 *   - A statement has the exact same namespace actions as the input actions list and no resource star
 *     - add resource to statement
 *
 *   - A statement contains all the actions as the input actions list AND has the star resource
 *     - do nothing
 *
 *   - No statement contains the same namespace actions as the input actions list AND there is no star resource statement
 *     - push a new statement to the policy
 */
function pushToPolicy(
  policy: AccessPolicyDocument,
  resource: string,
  actions: string[],
  effect: 'Allow' | 'Deny'
): void {
  const namespace = getResourceNamespace(resource);
  const starResource = getNamespaceStarResource(resource);

  const hasStarStatement = policy.Statement.some((s) => {
    return (
      s.Effect === effect &&
      s.Resource?.includes(starResource) &&
      arrayContainsAll(actions, getStatementNamespaceActions(s, namespace))
    );
  });

  if (!hasStarStatement) {
    const matchingActionsStatement = policy.Statement.find((s) => {
      return s.Effect === effect && setIsEqual(actions, getStatementNamespaceActions(s, namespace));
    });
    if (matchingActionsStatement?.Resource) {
      matchingActionsStatement.Resource.push(normalizeWildcards(resource));
    } else {
      policy.Statement.push({
        Effect: effect,
        Action: collapseActions(uniq(actions)),
        Resource: [normalizeWildcards(resource)]
      });
    }
  }
}

export const getAccessPolicyFromForm = (permissions: Permissions): AccessPolicyDocument => {
  const state: PolicyState = {
    allow: {},
    deny: {}
  };

  const resources = Object.keys(permissions.actions).filter(
    (action) => !action.startsWith('ts:graphql') && !action.startsWith('ts:shape')
  );
  const groups = groupBy(resources, getResourceNamespace);
  forEach(groups, (group, namespace) => {
    addNamespaceToPolicy(state, namespace, group, permissions);
  });

  const policy: AccessPolicyDocument = {
    Version: '2012-10-17',
    Statement: []
  };

  for (const resource of Object.keys(state.allow)) {
    if (state.allow[resource].length) {
      pushToPolicy(policy, resource, state.allow[resource], 'Allow');
    }
  }

  for (const resource of Object.keys(state.deny)) {
    if (state.deny[resource].length) {
      pushToPolicy(policy, resource, state.deny[resource], 'Deny');
    }
  }

  return policy;
};
